import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { AlertDetailResponse, Severity } from '@/types/alerts';

const SEVERITY_BADGE: Record<Severity, string> = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-blue-100 text-blue-800 border-blue-200',
  Watch: 'bg-slate-100 text-slate-700 border-slate-200',
  Normal: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

interface HeaderSectionProps {
  alert: AlertDetailResponse;
}

/**
 * Top-of-page summary chip strip + asset/card/driver display.
 *
 * Renders severity, risk score, rule_id, alert_time, status, asset_token,
 * card_token, driver_id, attendant_code, and the transaction_kind chip.
 * Null values render as "—" so the row never collapses awkwardly.
 */
export function HeaderSection({ alert }: HeaderSectionProps): JSX.Element {
  const dash = <span className="text-muted-foreground">—</span>;
  return (
    <section aria-labelledby="header-section">
      <h2 id="header-section" className="sr-only">
        Alert summary
      </h2>
      <div className="flex flex-wrap items-center gap-3">
        <Badge
          variant="outline"
          className={cn('font-medium', SEVERITY_BADGE[alert.severity])}
        >
          {alert.severity}
        </Badge>
        <span className="text-sm">
          <span className="text-muted-foreground">Risk </span>
          <span className="font-semibold">{alert.risk_score}</span>
        </span>
        <span className="font-mono text-xs text-muted-foreground">
          {alert.rule_id}
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date(alert.alert_time)
            .toISOString()
            .replace('T', ' ')
            .slice(0, 19)}
        </span>
        <Badge variant="secondary">{alert.status}</Badge>
        {alert.transaction_kind && (
          <Badge variant="outline" className="font-mono text-xs uppercase">
            {alert.transaction_kind}
          </Badge>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-muted-foreground">Asset</dt>
          <dd className="truncate font-mono">{alert.asset_token ?? dash}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Card</dt>
          <dd className="truncate font-mono">{alert.card_token ?? dash}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Driver</dt>
          <dd className="truncate font-mono">{alert.driver_id ?? dash}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Attendant</dt>
          <dd className="truncate font-mono">{alert.attendant_code ?? dash}</dd>
        </div>
      </dl>
    </section>
  );
}
